import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, map } from 'rxjs';

import {
  Company,
  CompanySummaryResponse,
  CompanyWeekSummary,
  PlannedUsedHoursChartDataResponse,
  RegisterCompany,
} from '@shared/models';

import { AuthService } from '../auth.service';
import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService,
    private authService: AuthService
  ) {
    super();
  }

  get(loader: string): Observable<Array<Company>> {
    this.loadersService.show(loader);
    return this.http
      .get<Array<Company>>(`${this.baseUrl}/companies`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getAll(loader: string): Observable<Array<Company>> {
    this.loadersService.show(loader);
    return this.http
      .get<Array<Company>>(`${this.baseUrl}/companies/all`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getByUser(loader: string, includes: (keyof Company | 'company')[] = []): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .get<Company>(`${this.baseUrl}/companies/my`, {
        params: httpParams().array('includes', includes).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getById(loader: string, id: string): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .get<Company>(`${this.baseUrl}/companies/${id}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getByIdWithIncludes(loader: string, id: string, includes: (keyof Company | 'company')[]): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .get<Company>(`${this.baseUrl}/companies/${id}/with-includes`, {
        params: httpParams().array('includes', includes).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  edit(loader: string, dto: Company, includes: (keyof Company | 'company')[]): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .put<Company>(this.baseUrl + '/companies', dto, {
        params: httpParams().array('includes', includes).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editActivityTemplates(loader: string, dto: Company, includes: (keyof Company | 'company')[]): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .put<Company>(this.baseUrl + '/companies/activity-templates', dto, {
        params: httpParams().array('includes', includes).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  editPhaseTemplates(loader: string, dto: Company, includes: (keyof Company | 'company')[]): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .put<Company>(this.baseUrl + '/companies/phase-templates', dto, {
        params: httpParams().array('includes', includes).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  add(loader: string, dto: Company): Observable<Company> {
    this.loadersService.show(loader);
    return this.http
      .post<Company>(this.baseUrl + '/companies', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/companies/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  getCompanySummary(loader: string, id?: string): Observable<CompanySummaryResponse> {
    this.loadersService.show(loader);
    return this.http
      .get<CompanySummaryResponse>(`${this.baseUrl}/companies/${id ? `${id}/` : ''}summary`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getWeekSummary(loader: string, week: string): Observable<CompanyWeekSummary> {
    this.loadersService.show(loader);
    return this.http
      .get<CompanyWeekSummary>(`${this.baseUrl}/companies/week-summary`, {
        params: httpParams().from({ week }).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getNumberOfActiveUsers(loader: string, id: string): Observable<number> {
    this.loadersService.show(loader);
    return this.http
      .get<number>(`${this.baseUrl}/companies/${id}/active-users`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getPlannedUsedHoursChartData(loader: string, fromDay: string, toDay: string, projectIds: string[]) {
    this.loadersService.show(loader);
    return this.http
      .get<PlannedUsedHoursChartDataResponse>(`${this.baseUrl}/companies/charts/planned-used-hours`, {
        params: httpParams().from({ fromDay, toDay }).array('projectIds', projectIds).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  registerCompany(loader: string, dto: RegisterCompany): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .post<void>(this.baseUrl + '/companies/register-company', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  activateCompany(loader: string, companyId: string): Observable<void> {
    this.loadersService.show(loader);
    return this.http
      .put<void>(this.baseUrl + '/companies/activate', { id: companyId })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }
}
